"use client";

import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import * as Sentry from "@sentry/nextjs";
import { captureClientException } from "@/lib/analytics";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Capture the route and digest alongside the error. Without these a
    // production crash arrives in Sentry as a bare minified message with no
    // way to tell WHICH route threw it or to correlate it with the server log
    // line — `digest` is the only shared id between the two, and it was being
    // dropped even though Next hands it to us (PANDA-1611).
    Sentry.withScope((scope) => {
      scope.setTag("error_boundary", "app");
      if (error.digest) scope.setTag("digest", error.digest);
      scope.setContext("route", {
        pathname: window.location.pathname,
        // The query string is frequently the trigger itself — an auto-submitted
        // ?prompt= is what reproduced this boundary's first real crash.
        search: window.location.search,
      });
      Sentry.captureException(error);
    });

    // Same crash, second destination. Sentry keeps the symbolicated frames;
    // PostHog Error Tracking is where this sits next to the person who hit it,
    // the replay of them hitting it, and the product failure events from the
    // same session. The envelope (route, project, session, deploy) is stamped
    // by `before_send` — only what THIS boundary uniquely knows is passed here.
    captureClientException(error, {
      failurePhase: "render:app",
      extra: { error_boundary: "app", digest: error.digest ?? null },
    });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4">
      <div className="flex flex-col items-center gap-5 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50">
          <AlertTriangle className="h-6 w-6 text-red-500" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-zinc-900">
            Something went wrong
          </h2>
          <p className="mt-2 max-w-md text-sm text-zinc-500">
            {error.message || "An unexpected error occurred."}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {/* A failed chunk download (name "ChunkLoadError", set by the
              Turbopack runtime) can't recover inside this document: the
              runtime caches the rejected load per URL, so reset() re-throws
              without making a request. Only a new document downloads it again,
              and reload() keeps the URL, so the open project stays selected.
              Click-only, so a chunk that keeps failing cannot loop. */}
          <button
            onClick={() => {
              if (error.name === "ChunkLoadError") {
                window.location.reload();
                return;
              }
              reset();
            }}
            className="rounded-full bg-zinc-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-800"
          >
            Try again
          </button>
          {/* `reset()` only re-renders the errored segment, so a DETERMINISTIC
              crash (bad data, a query param that re-triggers the same render)
              re-throws instantly and traps the user in a loop with no way out —
              exactly what happened in PANDA-1611. This is the guaranteed
              escape: a full document load, not a client navigation, because
              the router would otherwise re-render the same broken tree. */}
          <button
            onClick={() => window.location.assign("/projects")}
            className="rounded-full border border-zinc-200 bg-white px-5 py-2.5 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50"
          >
            Go to projects
          </button>
        </div>
        {/* Surfaced so QA can paste it into a ticket — it's the id that ties
            this screen to the Sentry event and the server log line. */}
        {error.digest ? (
          <p className="font-mono text-[11px] text-zinc-400">
            Error ID: {error.digest}
          </p>
        ) : null}
      </div>
    </div>
  );
}
